import React, { Fragment, useContext, useEffect, useReducer, useState } from 'react';
import Screen from 'utils/helpers/ScreenSize';
import { Dialog, Transition } from '@headlessui/react';
import { StoreContext } from 'pages/_app';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { MapIcon } from 'assets/img';
import classNames from 'classnames';
import AddressPickerModal from 'components/AddressPickerModal';
import { AutoComplete } from 'components/LandingPage/HeroSection/InputForm/AutoComplete';
import moment from 'moment';

function SingleAddressConfirmation({ visible, setModal }) {
  const screenType = Screen.ScreenType();
  const store = useContext(StoreContext);
  const cartStore = toJS(store?.cartStore);
  const userStore = toJS(store?.userStore);
  const router = useRouter();

  let { address, currentUser } = userStore;

  const initialState = {
    error: '',
    addressInput: address || '',
  };

  function reducer(state, action) {
    switch (action.type) {
      case 'reset':
        return initialState;
    }
    return {
      ...state,
      [action.field]: action.value,
    };
  }

  const [provider, dispatch] = useReducer(reducer, initialState);
  const [openSavedAddressModal, setOpenSavedAddressModal] = useState(false);

  useEffect(() => {
    dispatch({ field: 'error', value: '' });
    dispatch({ field: 'addressInput', value: address || '' });
  }, [visible, address]);

  const handleAddressChange = e => {
    dispatch({ field: 'addressInput', value: e.target.value });

    if (provider.error) {
      dispatch({ field: 'error', value: '' });
    }
  };

  const handleAddressSet = addressData => {
    if (addressData?.address) {
      dispatch({ field: 'addressInput', value: addressData.address });

      // Update the store with the parsed address data
      if (addressData?.completeAddress) {
        store.userStore?.setAddress(addressData.address, addressData.completeAddress);
      }

      if (provider.error) {
        dispatch({ field: 'error', value: '' });
      }
    }
  };

  const refreshRestaurantMenus = async () => {
    try {
      store.userStore?.setLoader(true);

      const currentDate = new Date();
      const currentDateTime = currentDate.toISOString().slice(0, 16).replace('T', ' ');

      const newDate = new Date();
      newDate.setDate(newDate.getDate() + 7);
      const deliveryDate = newDate.toISOString().slice(0, 10);

      const deliveryTime = '12:00 pm';

      const payload = {
        radius: 30,
        page: 1,
        per_page: 21,
        address: provider.addressInput,
        sort_by: '',
        current_date_time: currentDateTime,
        date: deliveryDate,
        time: deliveryTime,
      };

      const response = await store.userStore?.getLocationMenus(payload);

      if (response?.data) {
        store.userStore?.setLoader(false);

        try {
          router.replace(
            {
              pathname: router.pathname,
              query: {
                ...router.query,
                address: provider.addressInput,
                date: deliveryDate,
                time: deliveryTime,
              },
            },
            undefined,
            { shallow: true }
          );
        } catch (e) {
          console.log('error ', e);
        }

        setModal(false);
        setTimeout(() => {
          document.documentElement.removeAttribute('style');
        }, 0);

        store.userStore?.setIsNewAddress(true);
      } else {
        store.userStore?.setLoader(false);
        dispatch({
          field: 'error',
          value: 'Failed to refresh restaurant menus. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error refreshing restaurant menus:', error);
      store.userStore?.setLoader(false);
      dispatch({ field: 'error', value: 'Failed to refresh restaurant menus. Please try again.' });
    }
  };

  const continueClicked = () => {
    if (!provider.addressInput || provider.addressInput.trim() === '') {
      dispatch({ field: 'error', value: 'Please enter a valid address' });
      return;
    }

    refreshRestaurantMenus();
  };

  const submitButtons = (
    <div className="flex flex-col">
      <button
        type="submit"
        onClick={continueClicked}
        disabled={store.userStore?.loader}
        className={classNames({
          'flex h-12 min-w-[98px] px-4 py-2 justify-center items-center gap-2 self-stretch rounded-full text-white': true,
          'bg-[#575BE8] hover:bg-[#4A4DD8]': !store.userStore?.loader,
          'bg-gray-400 cursor-not-allowed': store.userStore?.loader,
        })}>
        {store.userStore?.loader ? 'Refreshing Restaurants...' : 'Continue with this address'}
      </button>
    </div>
  );

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          setModal(false);
          setTimeout(() => {
            document.documentElement.removeAttribute('style');
          }, 0);
        }}>
        {/* <form onSubmit={onContinuePress}> */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-screen sm:min-h-full sm:items-center sm:justify-center sm:p-4 md:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="flex flex-col w-full transform overflow-hidden bg-white text-left align-middle shadow-xl transition-all h-screen min-h-screen px-4 pt-8 pb-16 sm:h-auto sm:min-h-0 sm:w-[480px] sm:rounded-2xl sm:py-10 sm:px-8">
                {/* Mobile layout */}
                <div className="block sm:hidden flex flex-col h-full w-full">
                  <Dialog.Title
                    as="h3"
                    className="font-inter font-semibold text-[26px] leading-[39px] text-[var(--plain,#2D3958)]">
                    Confirm Delivery Address
                  </Dialog.Title>

                  <div className="flex flex-col items-start gap-6 self-stretch mt-2">
                    <p className="text-[var(--plain,#2D3958)] font-inter text-[16px] font-normal leading-[24px]">
                      The address you entered doesn't match any saved to your account. Would you
                      like to continue with this address or choose a different one from your saved
                      addresses?
                    </p>

                    <div className="flex flex-col items-start gap-1 self-stretch">
                      <span className="text-[#566985] font-inter text-[16px] font-medium leading-[24px]">
                        Entered Address
                      </span>
                      <div
                        className={classNames(
                          'autocomplete-container flex justify-between items-center self-stretch h-[48px] rounded-lg bg-white',
                          { 'border-red-500': provider.error, 'border-gray-300': !provider.error }
                        )}>
                        <span className="h-4 justify-start items-center flex pl-2">
                          <Image src={MapIcon} className="icon" alt="Map Icon for Address" />
                        </span>
                        <AutoComplete
                          addressError={provider.error}
                          handleOnChange={handleAddressChange}
                          router={router}
                          store={store}
                          userLogin={false}
                          address={provider.addressInput}
                          internalAddress={{ address: provider.addressInput }}
                          setAddress={handleAddressSet}
                        />
                      </div>
                      {provider.error && (
                        <span className="text-red-500 text-sm" aria-live="polite">
                          Please enter a valid address.
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto flex flex-col items-start gap-4 self-stretch bg-white">
                    <button
                      type="button"
                      onClick={continueClicked}
                      disabled={store.userStore?.loader}
                      className={classNames({
                        'flex h-11 min-w-[98px] px-4 py-2 justify-center items-center gap-2 self-stretch rounded-2xl text-white': true,
                        'bg-[#575BE8] hover:bg-[#4A4DD8]': !store.userStore?.loader,
                        'bg-gray-400 cursor-not-allowed': store.userStore?.loader,
                      })}>
                      {store.userStore?.loader
                        ? 'Refreshing Restaurants...'
                        : 'Continue with this address'}
                    </button>

                    <button
                      type="button"
                      onClick={() => setOpenSavedAddressModal(true)}
                      className="flex h-11 min-w-[98px] px-4 py-2 justify-center items-center gap-2 self-stretch rounded-2xl border border-[#575BE8] bg-white text-[#575BE8]"
                      aria-haspopup="dialog"
                      aria-label="Select saved address">
                      Select Saved Address
                    </button>

                    <AddressPickerModal
                      open={openSavedAddressModal}
                      setOpenSavedAddressModal={setOpenSavedAddressModal}
                      setModal={setModal}
                    />
                  </div>
                </div>

                {/* Desktop/Tablet layout (unchanged) */}
                <div className="hidden sm:block">
                  <div className="flex w-full">
                    <Dialog.Title
                      as="h3"
                      style={{
                        fontSize: screenType?.isTabletOrMobile ? 23 : 32,
                      }}
                      className="font-inter-semibold leading-6 text-black-light">
                      <span className="text-[24px]">Confirm Delivery Address</span>
                    </Dialog.Title>
                  </div>

                  <div className="flex flex-col justify-center items-center flex-wrap mt-4 md:w-full">
                    <p
                      id="dialog-description"
                      className="text-[var(--plain,#2D3958)] font-inter text-base font-normal leading-6 text-left mb-8 self-stretch">
                      The address you entered doesn't match any saved to your account. Would you
                      like to continue with this address or choose a different one from your saved
                      addresses?
                    </p>
                    <div className="flex flex-col w-full gap-8">
                      <div className="flex flex-col w-full">
                        <div
                          className={classNames(
                            'autocomplete-container flex justify-between items-center self-stretch h-[48px] rounded-lg bg-white',
                            { 'border-red-500': provider.error, 'border-gray-300': !provider.error }
                          )}>
                          <span className="h-4 justify-start items-center flex pl-2">
                            <Image src={MapIcon} className="icon" alt="Map Icon for Address" />
                          </span>
                          <AutoComplete
                            addressError={provider.error}
                            handleOnChange={handleAddressChange}
                            router={router}
                            store={store}
                            userLogin={false}
                            address={provider.addressInput}
                            internalAddress={{ address: provider.addressInput }}
                            setAddress={handleAddressSet}
                          />
                        </div>
                        {provider.error && (
                          <span className="text-red-500 text-sm" aria-live="polite">
                            Please enter a valid address.
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => setOpenSavedAddressModal(true)}
                        className="text-indigo-500 text-xs font-inter-normal leading-7 flex items-center h-11 px-2 py-2 gap-2"
                        aria-haspopup="dialog"
                        aria-label="Select saved address">
                        Select Saved Address
                      </button>

                      <AddressPickerModal
                        open={openSavedAddressModal}
                        setOpenSavedAddressModal={setOpenSavedAddressModal}
                        setModal={setModal}
                      />

                      <div className="hidden sm:flex flex-col">{submitButtons}</div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>

        {/* </form> */}
      </Dialog>
    </Transition>
  );
}

export default observer(SingleAddressConfirmation);
